import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input, Textarea } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { addChargeCategory, updateChargeCategory } from "@/services/chargeService";
import { CHARGE_ICONS } from "@/utils/charges";
import type { ChargeCategory } from "@/types";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: ChargeCategory | null;
}

export default function ChargeCategoryFormModal({ open, onClose, onSaved, editing }: Props) {
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(CHARGE_ICONS[1]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? "");
    setDescription(editing?.description ?? "");
    setIcon(editing?.icon ?? CHARGE_ICONS[1]);
  }, [open, editing]);

  const handleSave = async () => {
    if (!name.trim()) {
      notify("Le nom de la catégorie est obligatoire.", "error");
      return;
    }
    setBusy(true);
    const payload = { name: name.trim(), description: description.trim(), icon };
    if (editing) {
      await updateChargeCategory(editing.id, payload);
      notify("Catégorie mise à jour.");
    } else {
      await addChargeCategory(payload);
      notify(`Catégorie « ${name.trim()} » ajoutée.`);
    }
    setBusy(false);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Modifier la catégorie" : "Nouvelle catégorie de charges"}
      subtitle="Regroupe des charges, ex. Frais généraux, Charges de personnel..."
      icon="ri-price-tag-3-line"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={handleSave} disabled={busy}>
            {busy ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Nom de la catégorie"
          required
          placeholder="Ex : Frais généraux"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Textarea
          label="Description"
          rows={2}
          placeholder="À quoi correspond cette catégorie..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div>
          <p className="mb-2 text-xs font-medium text-foreground-700">Icône</p>
          <div className="flex flex-wrap gap-2">
            {CHARGE_ICONS.map((ic) => (
              <button
                key={ic}
                type="button"
                onClick={() => setIcon(ic)}
                className={`w-9 h-9 flex items-center justify-center rounded-md border transition-colors cursor-pointer ${
                  icon === ic
                    ? "border-primary-400 bg-primary-50 text-primary-700"
                    : "border-background-300 text-foreground-500 hover:bg-background-100"
                }`}
                aria-label={ic}
              >
                <i className={`${ic} text-lg`}></i>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}